import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import type { CaseStudy } from "@/types/case-study.types";
import { formatDuration } from "@/utils/case-study.utils";

interface CaseStudyCardProps {
  caseStudy: CaseStudy;
  variant?: string;
}

export function CaseStudyCard({ caseStudy }: CaseStudyCardProps) {
  const { metadata, client, overview, taxonomy, media, results } = caseStudy;

  return (
    <Link to={`/case-studies/${metadata.slug}`}>
      <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group">
        {/* Hero Image */}
        <div className="relative h-48 overflow-hidden rounded-t-lg">
          <img
            src={media.heroImage.url}
            alt={media.heroImage.alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {metadata.featured && (
            <div className="absolute top-4 right-4">
              <Badge className="bg-[#B87333] text-white">Featured</Badge>
            </div>
          )}
        </div>

        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-[#B87333] transition-colors">
            {overview.title}
          </CardTitle>
          <CardDescription className="text-base font-medium text-gray-600">
            {overview.tagline}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Summary */}
          <p className="text-sm text-gray-700 line-clamp-3">
            {overview.summary}
          </p>

          {/* Key Metric Highlight */}
          {results.keyMetrics.length > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{results.keyMetrics[0].icon}</span>
                <div>
                  <div className="text-2xl font-bold text-[#9b87f5]">
                    {results.keyMetrics[0].value}
                    {results.keyMetrics[0].unit && (
                      <span className="text-lg ml-1">
                        {results.keyMetrics[0].unit}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    {results.keyMetrics[0].label}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Technologies */}
          <div className="flex flex-wrap gap-2">
            {taxonomy.technologies.slice(0, 3).map((tech) => (
              <Badge key={tech} variant="outline" className="text-xs">
                {tech}
              </Badge>
            ))}
            {taxonomy.technologies.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{taxonomy.technologies.length - 3} more
              </Badge>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex items-center justify-between text-sm text-gray-600 border-t pt-4">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <span>{client.industryType}</span>
          </div>
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{formatDuration(overview.duration)}</span>
          </div>
        </CardFooter>
      </Card>
    </Link>
  );
}
